import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CanvasType, Plugin } from "types";
import { IFRAME_SANDBOX } from "./constants";
import { useIsAppBroken } from "./embeddedAppStore";
import { relocateIframe } from "./relocateIframe";

interface UseIframePlacementOptions {
    plugin: Plugin;
    isCanvasOpen: boolean;
    retryKey: number;
    iframeRef: React.MutableRefObject<HTMLIFrameElement | null>;
    handleLoad: () => void;
}

interface UseIframePlacementResult {
    iframeRootRef: React.RefObject<HTMLDivElement>;
    contentSlotRef: React.RefObject<HTMLDivElement>;
    contentSlotCallbackRef: (node: HTMLDivElement | null) => void;
    isBroken: boolean;
}

/**
 * Owns one embedded app's persistent iframe, reparenting the same node (never reloading) between
 * a hidden root and its visible canvas slot so app state and the RPC handshake survive open/close.
 * `none` apps move synchronously via `useLayoutEffect`; `drawer`/`modal` move via `useEffect`
 * after paint, so a cross-origin app doesn't measure a 0-width viewport mid-transition.
 */
export const useIframePlacement = ({
    plugin,
    isCanvasOpen,
    retryKey,
    iframeRef,
    handleLoad,
}: UseIframePlacementOptions): UseIframePlacementResult => {
    const { id: appId, name: appName, ui } = plugin;
    const { embeddedUrl = "", canvasType = CanvasType.DRAWER } = ui ?? {};

    const isBroken = useIsAppBroken(appId);

    const iframeRootRef = useRef<HTMLDivElement>(null);
    const contentSlotRef = useRef<HTMLDivElement>(null);
    const [contentSlotNode, setContentSlotNode] = useState<HTMLDivElement>();

    useEffect(() => {
        const root = iframeRootRef.current;
        if (!root || iframeRef.current) {
            return;
        }

        const iframe = document.createElement("iframe");
        iframe.title = appName;
        iframe.src = embeddedUrl;
        iframe.className = "h-full w-full border-0";
        iframe.setAttribute("sandbox", IFRAME_SANDBOX);
        iframe.addEventListener("load", handleLoad);
        iframeRef.current = iframe;
        root.append(iframe);

        return () => {
            iframe.removeEventListener("load", handleLoad);
            iframeRef.current = null;
            iframe.remove();
        };
    }, [retryKey, appName, embeddedUrl, handleLoad, iframeRef]);

    // Updating state (not a ref) re-triggers the move effect when the slot mounts.
    // On unmount rescue the iframe synchronously before headlessui removes the panel.
    const contentSlotCallbackRef = useRef((node: HTMLDivElement | null) => {
        setContentSlotNode(node ?? undefined);
        if (!node) {
            const iframe = iframeRef.current;
            const root = iframeRootRef.current;
            if (iframe && root) {
                relocateIframe(root, iframe);
            }
        }
    }).current;

    // NONE-type: move synchronously - show/hide is CSS-only, no headlessui transition delay.
    useLayoutEffect(() => {
        if (canvasType !== CanvasType.NONE) {
            return;
        }

        const iframe = iframeRef.current;
        const root = iframeRootRef.current;
        if (!iframe || !root) {
            return;
        }

        if (isCanvasOpen && !isBroken && contentSlotRef.current) {
            relocateIframe(contentSlotRef.current, iframe);
        } else if (!isCanvasOpen) {
            relocateIframe(root, iframe);
        }
    }, [isCanvasOpen, isBroken, canvasType, retryKey, iframeRef]);

    // NONE-type: rescue iframe before the contentSlot unmounts when isBroken flips.
    // The root container is stable for the component's lifetime, so capture it here; the iframe is
    // read inside cleanup because a retry reassigns iframeRef.current without re-running this effect,
    // and the cleanup must relocate the live node, not an already-removed one.
    useLayoutEffect(() => {
        if (canvasType !== CanvasType.NONE) {
            return;
        }

        const root = iframeRootRef.current;
        return () => {
            const iframe = iframeRef.current;
            if (iframe && root) {
                relocateIframe(root, iframe);
            }
        };
    }, [isBroken, canvasType, iframeRef]);

    // DRAWER/MODAL: move after headlessui applies CSS - useLayoutEffect would fire while the
    // panel is still display:none, giving cross-origin apps a 0-width viewport on init.
    // contentSlotNode is state so this re-runs when the slot mounts (may be a later commit).
    useEffect(() => {
        if (canvasType === CanvasType.NONE) {
            return;
        }

        const iframe = iframeRef.current;
        const root = iframeRootRef.current;
        if (!iframe || !root) {
            return;
        }

        if (isCanvasOpen && !isBroken && contentSlotNode) {
            relocateIframe(contentSlotNode, iframe);
        } else if (!isCanvasOpen) {
            relocateIframe(root, iframe);
        }
    }, [isCanvasOpen, isBroken, canvasType, contentSlotNode, retryKey, iframeRef]);

    return { iframeRootRef, contentSlotRef, contentSlotCallbackRef, isBroken };
};
